import { friendlyDbError } from '../utils/friendlyDbError';
import { create } from 'zustand';
import { supabase } from '../supabase';
import type {
  ExternalPartner,
  ExternalPartnerFormData,
  PartnersFilter,
  Referrer
} from '../types';
import { notifyPartnerProfilesChanged } from '../services/partnerReferrerProfilesService';
import { buildReferrerFromPartner, buildReferrerSyncFromPartner, type PartnerAnagraphic } from '../partners/partnerReferrerProfile';

/**
 * Crea il profilo provvigioni (riga referrers) di un partner che non lo ha.
 * Ogni partner deve averlo: e' quello che lo rende selezionabile come
 * segnalatore nelle offerte. Provvigione iniziale = quella del partner, o 0.
 */
async function insertReferrerProfile(partner: PartnerAnagraphic, organizationId: string, userId?: string | null): Promise<Referrer> {
  const { data, error } = await supabase
    .from('referrers')
    .insert(buildReferrerFromPartner(partner, organizationId, userId))
    .select()
    .single();
  if (error) throw error;
  return data as Referrer;
}

interface PartnerStore {
  // State
  partners: ExternalPartner[];
  selectedPartner: ExternalPartner | null;
  loading: boolean;
  error: string | null;
  
  // Filters
  filters: PartnersFilter;
  
  // Actions
  fetchPartners: () => Promise<void>;
  fetchPartnerById: (id: string) => Promise<void>;
  createPartner: (data: Partial<ExternalPartnerFormData>) => Promise<ExternalPartner | null>;
  updatePartner: (id: string, data: Partial<ExternalPartnerFormData>) => Promise<ExternalPartner | null>;
  deletePartner: (id: string) => Promise<boolean>;
  setFilters: (filters: PartnersFilter) => void;
  clearFilters: () => void;
  setSelectedPartner: (partner: ExternalPartner | null) => void;
  clearError: () => void;

  // Referrer integration
  generateReferrerFromPartner: (partnerId: string) => Promise<Referrer | null>;
  checkPartnerHasReferrer: (partnerId: string) => Promise<string | null>;
  createPartnerFromReferrer: (referrerId: string) => Promise<ExternalPartner | null>;
  fetchOrphanReferrers: () => Promise<Referrer[]>;
}

export const usePartnerStore = create<PartnerStore>((set, get) => ({
  // Initial state
  partners: [],
  selectedPartner: null,
  loading: false,
  error: null,
  filters: {},

  // Actions
  fetchPartners: async () => {
    try {
      set({ loading: true, error: null });

      // Get current user's organization_id
      const { data: userData, error: userError } = await supabase.auth.getUser();
      
      if (userError) throw userError;
      
      // Get user profile to get organization_id
      const { data: profileData, error: profileError } = await supabase
        .from('user_profiles')
        .select('organization_id')
        .eq('id', userData.user.id)
        .single();
      
      if (profileError) throw profileError;
      
      if (!profileData?.organization_id) {
        throw new Error('Organization ID not found for current user');
      }
      
      const { filters } = get();
      let query = supabase
        .from('external_partners')
        .select(`
          *,
          created_by_user:user_profiles!external_partners_created_by_fkey(
            id, full_name, email
          )
        `)
        .eq('organization_id', profileData.organization_id)
        .order('created_at', { ascending: false });

      // Apply filters (solo valori singoli: i filtri multipli della pagina
      // Partner sono array e vengono applicati lato client in PartnersPage)
      if (typeof filters.search === 'string' && filters.search) {
        query = query.or(`name.ilike.%${filters.search}%,company.ilike.%${filters.search}%,email.ilike.%${filters.search}%`);
      }
      
      if (typeof filters.partner_type === 'string' && filters.partner_type) {
        query = query.eq('partner_type', filters.partner_type);
      }
      
      if (typeof filters.status === 'string' && filters.status) {
        query = query.eq('status', filters.status);
      }
      
      if (typeof filters.business_sector === 'string' && filters.business_sector) {
        query = query.eq('business_sector', filters.business_sector);
      }

      const { data, error } = await query;

      if (error) throw error;

      // Calculate additional metrics for each partner
      const partnersWithMetrics = await Promise.all(
        (data || []).map(async (partner) => {
          // Get active tasks count
          const { count: activeTasksCount } = await supabase
            .from('partner_tasks')
            .select('*', { count: 'exact', head: true })
            .eq('partner_id', partner.id)
            .in('status', ['assigned', 'in_progress', 'under_review']);

          // Calculate completion rate
          const completionRate = partner.total_tasks_assigned > 0 
            ? Math.round((partner.total_tasks_completed / partner.total_tasks_assigned) * 100)
            : 0;

          return {
            ...partner,
            active_tasks_count: activeTasksCount || 0,
            completion_rate: completionRate
          };
        })
      );

      set({ partners: partnersWithMetrics, loading: false });
    } catch (error) {
      console.error('Error fetching partners:', error);
      set({ 
        error: friendlyDbError(error) || 'Errore nel caricamento dei partner',
        loading: false 
      });
    }
  },

  fetchPartnerById: async (id: string) => {
    try {
      set({ loading: true, error: null });

      // Get current user's organization_id
      const { data: userData, error: userError } = await supabase.auth.getUser();
      
      if (userError) throw userError;
      
      // Get user profile to get organization_id
      const { data: profileData, error: profileError } = await supabase
        .from('user_profiles')
        .select('organization_id')
        .eq('id', userData.user.id)
        .single();
      
      if (profileError) throw profileError;
      
      if (!profileData?.organization_id) {
        throw new Error('Organization ID not found for current user');
      }

      const { data, error } = await supabase
        .from('external_partners')
        .select(`
          *,
          created_by_user:user_profiles!external_partners_created_by_fkey(
            id, full_name, email
          ),
          updated_by_user:user_profiles!external_partners_updated_by_fkey(
            id, full_name, email
          )
        `)
        .eq('id', id)
        .eq('organization_id', profileData.organization_id)
        .single();

      if (error) throw error;

      // Get additional metrics
      const { count: activeTasksCount } = await supabase
        .from('partner_tasks')
        .select('*', { count: 'exact', head: true })
        .eq('partner_id', id)
        .in('status', ['assigned', 'in_progress', 'under_review']);

      const completionRate = data.total_tasks_assigned > 0 
        ? Math.round((data.total_tasks_completed / data.total_tasks_assigned) * 100)
        : 0;

      const partnerWithMetrics = {
        ...data,
        active_tasks_count: activeTasksCount || 0,
        completion_rate: completionRate
      };

      set({ selectedPartner: partnerWithMetrics, loading: false });
    } catch (error) {
      console.error('Error fetching partner:', error);
      set({ 
        error: friendlyDbError(error) || 'Errore nel caricamento del partner',
        loading: false 
      });
    }
  },

  createPartner: async (data: Partial<ExternalPartnerFormData>) => {
    try {
      set({ loading: true, error: null });

      // Get current user's organization
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error('User not authenticated');

      const { data: userProfile } = await supabase
        .from('user_profiles')
        .select('organization_id')
        .eq('id', userData.user.id)
        .single();

      if (!userProfile) throw new Error('User profile not found');

      const partnerData = {
        ...data,
        organization_id: userProfile.organization_id,
        created_by: userData.user.id,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      const { data: newPartner, error } = await supabase
        .from('external_partners')
        .insert(partnerData)
        .select(`
          *,
          created_by_user:user_profiles!external_partners_created_by_fkey(
            id, full_name, email
          )
        `)
        .single();

      if (error) throw error;

      // Profilo provvigioni creato subito: il partner e' gia' selezionabile come
      // segnalatore nelle offerte. Se fallisce il partner resta creato; il
      // profilo viene ricreato al primo salvataggio (vedi updatePartner).
      try {
        await insertReferrerProfile(newPartner, userProfile.organization_id, userData.user.id);
      } catch (referrerError) {
        console.error('[PartnerStore] Profilo provvigioni non creato:', referrerError);
      }

      // Add to local state
      set(state => ({
        partners: [{ ...newPartner, active_tasks_count: 0, completion_rate: 0 }, ...state.partners],
        loading: false
      }));

      return newPartner;
    } catch (error) {
      console.error('Error creating partner:', error);
      set({ 
        error: friendlyDbError(error) || 'Errore nella creazione del partner',
        loading: false 
      });
      return null;
    }
  },

  updatePartner: async (id: string, data: Partial<ExternalPartnerFormData>) => {
    try {
      set({ loading: true, error: null });

      // Get current user's organization_id for security
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error('User not authenticated');

      const { data: userProfile } = await supabase
        .from('user_profiles')
        .select('organization_id')
        .eq('id', userData.user.id)
        .single();

      if (!userProfile) throw new Error('User profile not found');

      const updateData = {
        ...data,
        updated_by: userData.user.id,
        updated_at: new Date().toISOString()
      };

      const { data: updatedPartner, error } = await supabase
        .from('external_partners')
        .update(updateData)
        .eq('id', id)
        .eq('organization_id', userProfile.organization_id)
        .select(`
          *,
          created_by_user:user_profiles!external_partners_created_by_fkey(
            id, full_name, email
          ),
          updated_by_user:user_profiles!external_partners_updated_by_fkey(
            id, full_name, email
          )
        `)
        .single();

      if (error) throw error;

      // Update local state
      set(state => ({
        partners: state.partners.map(partner =>
          partner.id === id ? { ...updatedPartner, active_tasks_count: partner.active_tasks_count, completion_rate: partner.completion_rate } : partner
        ),
        selectedPartner: state.selectedPartner?.id === id
          ? { ...updatedPartner, active_tasks_count: state.selectedPartner.active_tasks_count, completion_rate: state.selectedPartner.completion_rate }
          : state.selectedPartner,
        loading: false
      }));

      // Sincronizza l'anagrafica sul profilo provvigioni (la provvigione si
      // gestisce dalla scheda, qui non si tocca). Se manca, lo crea.
      try {
        const referrerId = await get().checkPartnerHasReferrer(id);
        if (referrerId) {
          const { error: syncError } = await supabase
            .from('referrers')
            .update({ ...buildReferrerSyncFromPartner(updatedPartner), updated_at: new Date().toISOString() })
            .eq('external_partner_id', id)
            .eq('organization_id', userProfile.organization_id);
          if (syncError) throw syncError;
        } else {
          await insertReferrerProfile(updatedPartner, userProfile.organization_id, userData.user.id);
        }
      } catch (syncError) {
        console.error('[PartnerStore] Sincronizzazione profilo provvigioni non riuscita:', syncError);
      }
      notifyPartnerProfilesChanged();

      return updatedPartner;
    } catch (error) {
      console.error('Error updating partner:', error);
      set({ 
        error: friendlyDbError(error) || 'Errore nell\'aggiornamento del partner',
        loading: false 
      });
      return null;
    }
  },

  deletePartner: async (id: string) => {
    try {
      set({ loading: true, error: null });

      // Get current user's organization_id for security
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error('User not authenticated');

      const { data: userProfile } = await supabase
        .from('user_profiles')
        .select('organization_id')
        .eq('id', userData.user.id)
        .single();

      if (!userProfile) throw new Error('User profile not found');

      const { error } = await supabase
        .from('external_partners')
        .delete()
        .eq('id', id)
        .eq('organization_id', userProfile.organization_id);

      if (error) throw error;

      // Update local state
      set(state => ({
        partners: state.partners.filter(partner => partner.id !== id),
        selectedPartner: state.selectedPartner?.id === id ? null : state.selectedPartner,
        loading: false
      }));

      return true;
    } catch (error) {
      console.error('Error deleting partner:', error);
      set({ 
        error: friendlyDbError(error) || 'Errore nell\'eliminazione del partner',
        loading: false 
      });
      return false;
    }
  },

  setFilters: (filters: PartnersFilter) => {
    set({ filters });
  },

  clearFilters: () => {
    set({ filters: {} });
  },

  setSelectedPartner: (partner: ExternalPartner | null) => {
    set({ selectedPartner: partner });
  },

  clearError: () => {
    set({ error: null });
  },

  // Check if a partner already has a referrer
  checkPartnerHasReferrer: async (partnerId: string) => {
    try {
      const { data, error } = await supabase
        .from('referrers')
        .select('id')
        .eq('external_partner_id', partnerId)
        .order('created_at', { ascending: true })
        .limit(1);

      if (error) throw error;

      // limit(1) e non maybeSingle: con due profili collegati maybeSingle va in
      // errore e il partner risultava senza profilo (se ne creava un terzo).
      return data?.[0]?.id || null;
    } catch (error) {
      console.error('Error checking partner referrer:', error);
      return null;
    }
  },

  // Generate a referrer from a partner
  generateReferrerFromPartner: async (partnerId: string) => {
    try {
      set({ loading: true, error: null });

      // 1. Check if partner already has a referrer
      const existingReferrerId = await get().checkPartnerHasReferrer(partnerId);
      if (existingReferrerId) {
        throw new Error('Questo partner ha già un referrer associato');
      }

      // 2. Get partner data
      const { data: partner, error: partnerError } = await supabase
        .from('external_partners')
        .select('*')
        .eq('id', partnerId)
        .single();

      if (partnerError) throw partnerError;
      if (!partner) throw new Error('Partner non trovato');

      // 3. Get current user's organization_id
      const { data: userData, error: userError } = await supabase.auth.getUser();
      if (userError) throw userError;

      const { data: profileData, error: profileError } = await supabase
        .from('user_profiles')
        .select('organization_id, id')
        .eq('id', userData.user.id)
        .single();

      if (profileError) throw profileError;
      if (!profileData?.organization_id) {
        throw new Error('Organization ID non trovato');
      }

      // 4. Create referrer with partner data (provvigione del partner o 0)
      const newReferrer = await insertReferrerProfile(partner, profileData.organization_id, profileData.id);

      // 5. Update partner with referrer_id (if field exists in future)
      // Note: referrer_id is not in the actual DB yet, but we're preparing for it
      await supabase
        .from('external_partners')
        .update({ updated_at: new Date().toISOString() })
        .eq('id', partnerId);

      set({ loading: false });

      console.log('[PartnerStore] Referrer generato con successo:', newReferrer.id);

      return newReferrer;
    } catch (error) {
      console.error('Error generating referrer from partner:', error);
      set({
        error: friendlyDbError(error) || 'Errore nella generazione del referrer',
        loading: false
      });
      return null;
    }
  },

  // Fetch orphan referrers (without external_partner_id)
  fetchOrphanReferrers: async () => {
    try {
      // Get current user's organization_id
      const { data: userData, error: userError } = await supabase.auth.getUser();
      if (userError) throw userError;

      const { data: profileData, error: profileError } = await supabase
        .from('user_profiles')
        .select('organization_id')
        .eq('id', userData.user.id)
        .single();

      if (profileError) throw profileError;
      if (!profileData?.organization_id) {
        throw new Error('Organization ID non trovato');
      }

      const { data, error } = await supabase
        .from('referrers')
        .select('*')
        .eq('organization_id', profileData.organization_id)
        .is('external_partner_id', null)
        .order('created_at', { ascending: false });

      if (error) throw error;

      return data || [];
    } catch (error) {
      console.error('Error fetching orphan referrers:', error);
      return [];
    }
  },

  // Create a partner from an orphan referrer
  createPartnerFromReferrer: async (referrerId: string) => {
    try {
      set({ loading: true, error: null });

      // 1. Get referrer data
      const { data: referrer, error: referrerError } = await supabase
        .from('referrers')
        .select('*')
        .eq('id', referrerId)
        .single();

      if (referrerError) throw referrerError;
      if (!referrer) throw new Error('Segnalatore non trovato');

      // Check if referrer already has a partner
      if (referrer.external_partner_id) {
        throw new Error('Questo segnalatore è già collegato a un partner');
      }

      // 2. Get current user's organization
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error('User not authenticated');

      const { data: userProfile } = await supabase
        .from('user_profiles')
        .select('organization_id')
        .eq('id', userData.user.id)
        .single();

      if (!userProfile) throw new Error('User profile not found');

      // 3. Create partner with referrer data
      const partnerData = {
        organization_id: userProfile.organization_id,
        name: referrer.full_name,
        email: referrer.email,
        phone: referrer.phone,
        tax_code: referrer.tax_code,
        vat_number: referrer.vat_number,
        commission_rate: referrer.commission_rate,
        partner_type: 'Segnalatore',
        status: 'active',
        notes: referrer.notes,
        created_by: userData.user.id,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      const { data: newPartner, error: partnerError } = await supabase
        .from('external_partners')
        .insert(partnerData)
        .select(`
          *,
          created_by_user:user_profiles!external_partners_created_by_fkey(
            id, full_name, email
          )
        `)
        .single();

      if (partnerError) throw partnerError;

      // 4. Link referrer to the new partner
      const { error: updateError } = await supabase
        .from('referrers')
        .update({
          external_partner_id: newPartner.id,
          updated_at: new Date().toISOString()
        })
        .eq('id', referrerId);

      if (updateError) throw updateError;

      // 5. Add to local state
      set(state => ({
        partners: [{ ...newPartner, active_tasks_count: 0, completion_rate: 0 }, ...state.partners],
        loading: false
      }));

      console.log('[PartnerStore] Partner creato da referrer:', newPartner.id);

      return newPartner;
    } catch (error) {
      console.error('Error creating partner from referrer:', error);
      set({
        error: friendlyDbError(error) || 'Errore nella creazione del partner',
        loading: false
      });
      return null;
    }
  }
}));